import { WAVEFORM_BUCKETS } from '../constants';

/**
 * Downsamples raw PCM to the 64 normalised buckets the wire format carries.
 * RMS (not peak) so quiet speech still renders a readable bar.
 */
export function bucketsFromPcm(samples: Float32Array, buckets = WAVEFORM_BUCKETS): number[] {
  if (samples.length === 0) return new Array<number>(buckets).fill(0);
  const width = Math.max(1, Math.floor(samples.length / buckets));
  const out: number[] = [];
  let peak = 0;

  for (let b = 0; b < buckets; b += 1) {
    const start = b * width;
    let sum = 0;
    let count = 0;
    for (let i = start; i < start + width && i < samples.length; i += 1) {
      const value = samples[i] ?? 0;
      sum += value * value;
      count += 1;
    }
    const rms = count > 0 ? Math.sqrt(sum / count) : 0;
    peak = Math.max(peak, rms);
    out.push(rms);
  }

  const scale = peak > 0 ? 100 / peak : 0;
  return out.map((value) => Math.min(100, Math.round(value * scale)));
}

/** Live meter reading (0..100) from an analyser's time-domain buffer. */
export function levelFromTimeDomain(buffer: Uint8Array): number {
  let sum = 0;
  for (let i = 0; i < buffer.length; i += 1) {
    const centred = ((buffer[i] ?? 128) - 128) / 128;
    sum += centred * centred;
  }
  const rms = Math.sqrt(sum / Math.max(1, buffer.length));
  return Math.min(100, Math.round(rms * 260));
}

/** Pads or trims a waveform so playback bars never reflow. */
export function normaliseWaveform(waveform: readonly number[], buckets = WAVEFORM_BUCKETS): number[] {
  if (waveform.length === buckets) return [...waveform];
  const out: number[] = [];
  for (let i = 0; i < buckets; i += 1) {
    const source = waveform[Math.floor((i / buckets) * waveform.length)] ?? 0;
    out.push(Math.max(0, Math.min(100, source)));
  }
  return out;
}
